import React, { useEffect } from 'react';
import { CalendarCheck, X } from 'lucide-react';

const HEADER_HEIGHT_PX = 72;

/**
 * RDVBookingBubble — booking bubble + iframe modal (simplified).
 *
 * iOS Safari 15.4+ understands 100dvh natively and shrinks the overlay
 * automatically when the on-screen keyboard appears. The iframe (which is
 * a flex-1 child) shrinks with it, so the CRM widget's own sticky input
 * always lands above the keyboard. No visualViewport JS gymnastics needed.
 *
 * The only DOM mutation we make while open is `overflow: hidden` on
 * body + html — enough to lock background scroll, nothing more.
 */
const RDVBookingBubble = ({ open, onOpenChange, pageSource }) => {
  // SIMPLE body scroll lock — nothing else.
  useEffect(() => {
    if (!open) return;
    // Meta Pixel — fire on open: Schedule (RDV intent) + Lead (Léa widget engaged)
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'Schedule', { source: pageSource || 'main' });
      window.fbq('track', 'Lead', { source: pageSource || 'main' });
    }
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [open, pageSource]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  return (
    <>
      {/* Floating bubble + label */}
      <div
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 select-none"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
        data-testid="rdv-bubble-wrapper"
      >
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          className="hidden sm:inline-flex items-center gap-2 bg-white text-gray-900 pl-4 pr-5 py-3 rounded-full shadow-xl border border-gray-200 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 font-semibold text-sm group"
          data-testid="rdv-bubble-label"
          aria-label="Prendre rendez-vous avec Léa"
        >
          <span className="w-2 h-2 rounded-full bg-[#52a675] animate-pulse" aria-hidden="true" />
          <span>Prendre rendez-vous</span>
          <span className="text-xs text-[#52a675] font-bold group-hover:translate-x-0.5 transition-transform">→</span>
        </button>

        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-label="Ouvrir la prise de rendez-vous"
          className="w-16 h-16 rounded-full bg-gradient-to-br from-[#52a675] to-[#3d8a5c] text-white shadow-2xl hover:shadow-[0_20px_50px_rgba(82,166,117,0.6)] hover:scale-110 transition-all duration-300 flex items-center justify-center ring-4 ring-white relative"
          data-testid="rdv-bubble-button"
        >
          <CalendarCheck className="w-7 h-7" strokeWidth={2.2} />
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-[10px] font-bold text-gray-900 flex items-center justify-center shadow-md ring-2 ring-white">
            ●
          </span>
        </button>
      </div>

      {/* Modal — native 100dvh, no JS sizing */}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-4"
          style={{ height: '100dvh', maxHeight: '100dvh' }}
          role="dialog"
          aria-modal="true"
          aria-label="Prise de rendez-vous"
          data-testid="rdv-modal"
          onClick={(e) => {
            // Click on backdrop closes the modal; clicks inside the card don't propagate
            if (e.target === e.currentTarget) onOpenChange(false);
          }}
        >
          <div
            className="bg-white shadow-2xl overflow-hidden flex flex-col w-full
                       md:max-w-2xl md:rounded-2xl md:h-auto md:max-h-[88dvh]
                       animate-in slide-in-from-bottom md:fade-in md:zoom-in-95 duration-300"
            style={{ height: '100%' }}
            data-testid="rdv-modal-panel"
          >
            {/* Header — fixed height, never scrolls away */}
            <div
              className="bg-gradient-to-r from-[#52a675] to-[#3d8a5c] text-white px-5 flex items-center justify-between shrink-0 shadow-md"
              style={{
                height: `${HEADER_HEIGHT_PX}px`,
                paddingTop: 'max(0px, env(safe-area-inset-top))',
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center ring-2 ring-white/30 shrink-0">
                  <CalendarCheck className="w-5 h-5" />
                </div>
                <div className="leading-tight min-w-0">
                  <p className="text-base font-semibold truncate">Léa — Assistant RDV</p>
                  <p className="text-[12px] text-white/85 truncate">CarMaintenance Meyrin · Réponse instantanée</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-white/15 rounded-lg transition-colors shrink-0"
                aria-label="Fermer la prise de rendez-vous"
                data-testid="rdv-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Iframe area — flex:1 + min-h:0 lets iOS shrink it natively with the keyboard */}
            <div className="flex-1 min-h-0 bg-white">
              <iframe
                src={`https://crm.car-main.ch/widget/rdv${pageSource ? `?source=${encodeURIComponent(pageSource)}` : ''}`}
                title="Prise de rendez-vous CarMaintenance Meyrin"
                className="w-full h-full block"
                style={{ border: 0, display: 'block' }}
                allow="clipboard-write; geolocation"
                data-testid="rdv-modal-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RDVBookingBubble;
